/**
 * AuthLayout — the single authenticated app shell.
 *
 * Complete auth scenario matrix:
 *
 *  ┌──────────────────────────────────────────────────────────────────────────┐
 *  │  SCENARIO                              │  OUTCOME                        │
 *  ├──────────────────────────────────────────────────────────────────────────┤
 *  │  Standalone SID+SIG cookies present    │  → /standalone/products (hard)  │
 *  │  Auth0 SDK still initialising          │  Full-screen loader             │
 *  │  Token validation in flight            │  Full-screen loader             │
 *  │  Not authenticated (no Auth0 session)  │  → / login (replace)            │
 *  │  Token fetch threw (network / expired) │  Clear storage → logout → /     │
 *  │  Token returned empty string           │  Clear storage → logout → /     │
 *  │  Authenticated + valid token           │  Render full app shell ✓        │
 *  └──────────────────────────────────────────────────────────────────────────┘
 *
 * Sidebar state is persisted via the settings util (sidebarLocked / sidebarOpen).
 * On token failure users are silently logged out → redirected to login for a
 * clean re-auth flow (no intermediate error modal).
 */
import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import Loading from '@/components/atoms/Loader';
import { AuthContext } from '@/context/auth.context';

import { getCookie } from '@/standalone';
import { APP_BG } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';
import { EStandalonePages, MAGIC_COOKIES } from '@/pages/StandAlone/constants';
import { getSettings, updateSettings } from '@/utils/settings';
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import FooterVisibility from './FooterVisibility';

// ─── Component ────────────────────────────────────────────────────────────────
const AuthLayout: React.FC = () => {
  const { isLoading, isAuthenticated } = useAuth0();
  const { tokenStatus } = useContext(AuthContext);

  // ── Sidebar (persisted) ───────────────────────────────────────────────────
  const [isSidebarExpandEnabled, setIsSidebarExpandEnabled] = useState(
    () => getSettings().sidebarLocked,
  );
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [isSidebarManuallyCollapsed, setIsSidebarManuallyCollapsed] = useState(
    () => !getSettings().sidebarOpen,
  );
  // ── Mobile drawer (< md breakpoint) ──────────────────────────────────────
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const location = useLocation();
  // Close drawer on every navigation (covers programmatic navigate() calls too)
  useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [location.pathname]);

  // ── Sidebar callbacks ─────────────────────────────────────────────────────
  const handleToggleSidebarExpandMode = useCallback((enabled: boolean) => {
    setIsSidebarExpandEnabled(enabled);
    setIsSidebarHovered(false);
    setIsSidebarManuallyCollapsed(false);
    updateSettings({ sidebarLocked: enabled, sidebarOpen: true });
  }, []);

  const handleToggleSidebarOpen = useCallback(() => {
    setIsSidebarManuallyCollapsed((prev) => {
      const next = !prev;
      updateSettings({ sidebarOpen: !next });
      return next;
    });
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  // Guards — evaluated in priority order (all hooks must be declared above)
  // ══════════════════════════════════════════════════════════════════════════

  // 1. Standalone SID+SIG session — skip auth entirely
  if (getCookie(MAGIC_COOKIES.SID) && getCookie(MAGIC_COOKIES.SIG)) {
    return <Navigate to={EStandalonePages.APPROVED_PRODUCTS} replace />;
  }

  // 2. Auth0 SDK still initialising
  if (isLoading) return <Loading />;

  // 3. No Auth0 session → send to login
  if (!isAuthenticated) return <Navigate to="/" replace />;

  // 4. Token check in flight (or invalid — logout already triggered)
  if (tokenStatus !== 'valid') return <Loading />;

  // ══════════════════════════════════════════════════════════════════════════
  // Render — only reached when fully authenticated with a valid token
  // ══════════════════════════════════════════════════════════════════════════

  const isSidebarOpen = isSidebarExpandEnabled
    ? !isSidebarManuallyCollapsed
    : isSidebarHovered;

  return (
    <div
      data-testid={tid('auth-layout')}
      className={`flex h-screen w-full overflow-hidden ${APP_BG.canvas} transition-colors duration-300`}
    >
      {/* ── Mobile overlay backdrop ───────────────────────────────────── */}
      {isMobileDrawerOpen && (
        <div
          className="fixed inset-0 z-[1050] bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileDrawerOpen(false)}
        />
      )}

      {/* ── Sidebar — desktop: hover-expand wrapper | mobile: fixed drawer ── */}
      <div
        className={`
          md:relative md:flex md:shrink-0
          fixed inset-y-0 left-0 z-[1100]
          transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]
          md:translate-x-0
          ${isMobileDrawerOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        onMouseEnter={() =>
          !isSidebarExpandEnabled && setIsSidebarHovered(true)
        }
        onMouseLeave={() =>
          !isSidebarExpandEnabled && setIsSidebarHovered(false)
        }
        style={{ contain: 'layout style', willChange: 'transform' }}
      >
        <Sidebar
          isOpen={isMobileDrawerOpen || isSidebarOpen}
          isExpandEnabled={isSidebarExpandEnabled}
          onToggleExpandEnabled={handleToggleSidebarExpandMode}
          onToggleOpen={handleToggleSidebarOpen}
          onMobileClose={() => setIsMobileDrawerOpen(false)}
        />
      </div>

      {/* ── Main content column ──────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0">
        <Header onMobileMenuToggle={() => setIsMobileDrawerOpen((p) => !p)} />
        <FooterVisibility />
      </div>
    </div>
  );
};

export default AuthLayout;
